/* eslint-disable no-unused-vars */
import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';

import { useMutation } from 'react-query';
import { useMemo } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { downloadFile } from 'apis/proposals.api';
import {
  TextareaInput,
  Select,
  HugerteEditor,
  TextInput,
  MultipleSelect,
} from 'components/FormControl';
import { getProjectTypes } from 'utils/func';

export default function ProposalForm({
  disabled,
  researchFields,
  control,
  errors,
  proposalDetailData,
}) {
  const { t } = useTranslation();

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const projectTypes = getProjectTypes(t, true);

  const handleDownloadFile = (path) => {
    mutateDownloadFile(
      { path, id: proposalDetailData?.id },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            // eslint-disable-next-line prefer-destructuring
            link.download = path.split('__')[1];

            link.click();
          }
        },
      }
    );
  };

  const typeProposalOptions = [
    { name: t('proposal.typeProposal.topic'), value: 'topic' },
    { name: t('proposal.typeProposal.project'), value: 'project' },
    { name: t('proposal.typeProposal.productionProject'), value: 'production_project' },
  ];

  const publicProfileId = useMemo(
    () => proposalDetailData?.owner?.scientificProfile?.publicProfile?.publicProfileId ?? null,
    [proposalDetailData]
  );
  // #endregion Data

  // #region Event
  // #endregion Event

  return (
    <div className="relative">
      <h4 className="text-center mb-4">{t('proposal.create.label')}</h4>
      <div className="grid p-fluid">
        <div className="col-12">
          <TextareaInput
            label={t('proposal.create.name')}
            name="vietnameseName"
            isRequired
            readOnly={disabled}
            control={control}
            errors={errors}
          />
        </div>
        <div className="col-9">
          <MultipleSelect
            label={t('proposal.create.researchField')}
            name="researchFieldIds"
            inputField="id"
            renderField="name"
            options={researchFields}
            isRequired
            readOnly={disabled}
            control={control}
            errors={errors}
          />
        </div>
        <div className="col-12 grid">
          <div className="col-3">
            <TextareaInput
              label={t('proposal.create.yearProposed')}
              name="yearProposed"
              isRequired
              readOnly={disabled}
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-3">
            <Select
              label={t('proposal.create.typeProposal')}
              name="type"
              isRequired
              options={typeProposalOptions}
              inputField="value"
              readOnly={disabled}
              control={control}
              errors={errors}
            />
          </div>
          <div className="col-3">
            <Select
              label={t('proposalList.projectType')}
              name="projectType"
              options={projectTypes}
              inputField="value"
              readOnly={disabled}
              control={control}
              errors={errors}
            />
          </div>
        </div>
        <div className="col-3">
          <div className="mb-1">
            <span>{t('projectProposal.general.researchHumanResources.ownerFields.label')} </span>
            <span className="text-red-500" style={{ fontWeight: 900 }}>
              *
            </span>
            {publicProfileId && (
              <a
                href={`${process.env.REACT_APP_SCIENTIST_URL}/profile/${publicProfileId}`}
                target="_blank"
                rel="noreferrer"
              >
                <i
                  className="view-public-profile pi pi-eye text-primary cursor-pointer bg-primary p-1 border-circle ml-1 "
                  data-pr-tooltip={t('proposal.viewPublicProfile')}
                  data-pr-position="right"
                />
                <Tooltip target=".view-public-profile" />
              </a>
            )}
          </div>

          <TextInput name="fullname" control={control} errors={errors} readOnly={disabled} />
        </div>
        <div className="col-12">
          <HugerteEditor
            label={t('proposal.create.target')}
            name="target"
            isRequired
            control={control}
            errors={errors}
            readOnly
          />
        </div>

        <div className="col-12">
          <h5>{t('proposal.create.uploadFiles')}</h5>
        </div>
        <div className="col-12">
          {proposalDetailData?.attachmentFileUrl && (
            <span
              className="p-link text-primary underline"
              onClick={
                () => handleDownloadFile(proposalDetailData.attachmentFileUrl)
                // eslint-disable-next-line react/jsx-curly-newline
              }
            >
              {proposalDetailData.attachmentFileUrl.split('__')[1]}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

ProposalForm.propTypes = {
  disabled: PropTypes.bool,
  researchFields: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  proposalDetailData: PropTypes.object,
};

ProposalForm.defaultProps = {
  disabled: false,
  proposalDetailData: {},
};
